import { Box, IconButton, Menu, Chip } from '@mui/material';
import { FC, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { FirebaseError } from 'firebase/app';
import { useSnackbar } from 'notistack';
import { IBoardColumnShortDataUI, IColumnUI } from 'data/types/board.type';
import { useDeleteBoardColumnMutation } from 'redux/apiSlices/board.slice';
import { errorMessages, successMessages } from 'data/messages/messages';
import ChangeColor from './ChangeColor';
import EditName from './EditName';
import RemoveColumnAction from './RemoveColumn/RemoveColumnAction';
import RemoveColumnConfirmModal from './RemoveColumn/RemoveColumnConfirmModal';

const ColumnHeader: FC<{
  column: IColumnUI;
  boardId: string;
  projectId: string;
  dragHandleProps?: any;
  allColumnsShortData: IBoardColumnShortDataUI[];
}> = ({ column, boardId, projectId, dragHandleProps, allColumnsShortData }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [openComfirmModal, setOpenConfirmModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteColumn, { isLoading: deleteLoading }] =
    useDeleteBoardColumnMutation();

  const onDeleteColumn = async (moveTasksTo: string | null) => {
    try {
      await deleteColumn({
        columnId: column.documentId,
        boardId,
        projectId,
        moveTasksTo,
      }).unwrap();
      setAnchorEl(null);
      enqueueSnackbar(successMessages.toast, {
        variant: 'success',
      });
    } catch (err) {
      const error = err as FirebaseError;
      enqueueSnackbar(error.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };

  const openConfirmModal = () => {
    setOpenConfirmModal(true);
  };
  const closeConfirmModal = () => {
    setOpenConfirmModal(false);
  };

  const taskCount = column.tasks.length;
  const possibleColumnsToMove = allColumnsShortData.filter(
    c => c.documentId !== column.documentId,
  );

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      {...dragHandleProps}
      sx={{
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flex={1}
        minWidth={0}
      >
        <DragIndicatorIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            opacity: 0.5,
            flexShrink: 0,
          }}
        />
        <Box flex={1} minWidth={0}>
          <EditName
            boardId={boardId}
            column={column}
            projectId={projectId}
          />
        </Box>
        <Chip
          label={taskCount}
          size="small"
          sx={{
            height: 22,
            minWidth: 28,
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        />
      </Box>
      <IconButton
        size="small"
        onClick={e => {
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          ml: 0.5,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <RemoveColumnAction
          hasTasks={column.tasks.length > 0}
          onDeleteColumn={onDeleteColumn}
          openConfirmModal={openConfirmModal}
          deleteLoading={deleteLoading}
        />
        <ChangeColor boardId={boardId} column={column} projectId={projectId} />
      </Menu>
      <RemoveColumnConfirmModal
        open={openComfirmModal}
        onCloseModal={closeConfirmModal}
        onDeleteColumn={onDeleteColumn}
        submitButtonLoading={deleteLoading}
        columnsToMove={possibleColumnsToMove}
      />
    </Box>
  );
};

export default ColumnHeader;
